import React from 'react';
import { Map } from 'immutable';
import { observer } from 'mobx-react';
import cx from 'classnames';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import withStores from '@studionand/ada-next/src/stores/withStores';

const useStyles = makeStyles(theme => ({
  wrap: {
    padding: theme.spacing(1),
    // outline: `red 1px solid`,
  },
  quote: {
    borderLeft: `${theme.editorPalette.primary.main} 2px solid`,
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  list: {
    margin: theme.spacing(0, 4),
    padding: 0,
    // listStyleType: 'none',
  },
  listItem: {
    padding: theme.spacing(0, 0, 0, 5),
    margin: 0,
    marginBottom: theme.spacing(1),
  },
  frameBlock: {
    padding: theme.spacing(5),
    border: `${theme.editorPalette.primary.main} 2px solid`,
    borderLeftWidth: 4,
  },
  link: {
    color: theme.editorPalette.primary.main,
    '&:hover': {
      color: theme.editorPalette.primary.dark,
    },
  },
}));

const GridWrap = ({ children, className, ...props }) => {
  const classes = useStyles();
  return (
    <div {...props} className={cx(classes.wrap, className)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {children}
        </Grid>
      </Grid>
    </div>
  );
};

const CustomTextBlock = ({
  typographyProps,
  children,
  className,
  ...props
}) => {
  return (
    <Typography
      component="div"
      variant="h6"
      className={cx(className)}
      {...typographyProps}
      {...props}
    >
      {children}
    </Typography>
  );
};

const Ul = ({ className, ...props }) => {
  const classes = useStyles();
  return <ul className={cx(className, classes.list)} {...props} />;
};
const Ol = ({ className, ...props }) => {
  const classes = useStyles();
  return <ol className={cx(className, classes.list)} {...props} />;
};

const Li = ({ className, ...props }) => {
  const classes = useStyles();
  return (
    <Typography
      className={cx(className, classes.listItem)}
      {...props}
      component="li"
      variant="h6"
    />
  );
};

const FrameBlock = ({ className, ...props }) => {
  const classes = useStyles();
  return (
    <Typography
      className={cx(className, classes.frameBlock)}
      {...props}
      color="primary"
      component="div"
      variant="h6"
    />
  );
};

const Quote = ({ className, ...props }) => {
  const classes = useStyles();
  return (
    <CustomTextBlock
      {...props}
      typographyProps={{
        variant: 'h6',
        className: cx(className, classes.quote),
      }}
    />
  );
};

const createLinkedCustomTextBlock = prefix =>
  withStores('appStore')(
    observer(({ typographyProps, className, ...props }) => {
      const classes = useStyles();
      const editMode = props.appStore.editMode;
      const block = props.children.props.block;

      return (
        <CustomTextBlock
          {...props}
          className={cx(className, { [classes.link]: !editMode })}
          typographyProps={{
            variant: 'body1',
            align: 'center',
            href: `${prefix}${block.getText().trim()}`,
            target: '_blank',
            component: !editMode ? 'a' : 'div',
            ...typographyProps,
          }}
        />
      );
    }),
  );

const createPlugin = (isDownloadable = true) => {
  let editMode = false;

  return {
    blockStyleFn: (block, { getReadOnly, getProps }) => {
      const { page, appStore } = getProps();
      const editMode = appStore.editMode;
      const blockData = block.getData().toJS();
      const type = block.getType();
      return cx(
        type,
        blockData.alignment,
        page.type,
        { editMode },
        { isEmpty: block.getText().length === 0 },
      );
    },
    blockRendererFn: block => {
      const type = block.getType();
      return null;
    },
    blockRenderMap: Map({
      unstyled: {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h6' }} />
        ),
      },
      caption: {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'caption' }}
          />
        ),
      },
      title: {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'h1', align: 'center' }}
          />
        ),
      },
      'header-one': {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'h2', align: 'center' }}
          />
        ),
      },
      'header-two': {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h4' }} />
        ),
      },
      'header-three': {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h4' }} />
        ),
      },
      subtitle: {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'subtitle2', align: 'center' }}
          />
        ),
      },
      place: {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'h6', align: 'center' }}
          />
        ),
      },
      website: {
        element: createLinkedCustomTextBlock('http://'),
      },
      address: {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'body1', align: 'center' }}
          />
        ),
      },
      phone: {
        element: createLinkedCustomTextBlock('tel:'),
      },
      email: {
        element: createLinkedCustomTextBlock('mailto:'),
      },
      'header-four': {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h5' }} />
        ),
      },
      'header-five': {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h6' }} />
        ),
      },
      'header-six': {
        element: props => (
          <CustomTextBlock
            {...props}
            typographyProps={{ variant: 'subtitle1' }}
          />
        ),
      },
      blockquote: {
        element: Quote,
      },
      'frame-block': {
        element: FrameBlock,
      },
      'code-block': {
        element: props => (
          <CustomTextBlock {...props} typographyProps={{ variant: 'h6' }} />
        ),
      },
      'unordered-list-item': {
        wrapper: <Ul />,
        element: Li,
      },
      'ordered-list-item': {
        wrapper: <Ol />,
        element: Li,
      },
    }),
  };
};

export default createPlugin;
